import { Factory } from "@/data/dao/Factory";
import { LocationDao } from "@/data/dao/service/LocationDao";
import { SubscriptionDao } from "@/data/dao/service/SubscriptionDao";
import type { ISubscription } from "@/data/model/Subscription";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";

import { i18n } from "@/portal/Trans";
import { DataTable, type IDataTableConfig } from "@/portal/elements/DataTable";
import { TableFilter } from "@/portal/elements/TableFilter";
import { getLocationName } from "@/shared/helpers/LocationHelper";

export class SubscriptionTableController {
    private $element: JQuery;
    private dao: SubscriptionDao;

    private dataTable: DataTable<ISubscription>;
    private search: TableFilter;

    constructor(element: JQuery) {
        this.$element = element;

        this.dao = Factory.getInstance<SubscriptionDao>(SubscriptionDao);

        const tableConfig: IDataTableConfig<ISubscription> = {
            columnMapping: [
                {
                    name: i18n("general.entities.location"),
                    property: "locationName",
                    styles: { width: "15%" },
                    formatter: (m) => {
                        return $(
                            '<a href="/location/' + m.locationId + '">' + Format.htmlEscape(m.locationName) + "</a>"
                        ).click((e) => {
                            e.stopPropagation();
                        });
                    },
                },
                {
                    name: i18n("subscription.fields.preset"),
                    property: "presetName",
                    styles: { width: "12%" },
                    formatter: (m, p) => {
                        if (m.presetId === undefined) return i18n("subscription.customForCustomer");

                        return Format.htmlEscape(p);
                    },
                },
                {
                    name: i18n("subscription.fields.covid"),
                    property: "canUseCovid",
                    styles: { width: "8%" },
                    formatter: (m, p) => (p ? i18n("general.yes") : i18n("general.no")),
                },
                {
                    name: i18n("subscription.fields.documents"),
                    property: "canUseDocuments",
                    styles: { width: "8%" },
                    formatter: (m, p) => (p ? i18n("general.yes") : i18n("general.no")),
                },
                {
                    name: i18n("subscription.fields.pay"),
                    property: "canUseOrder",
                    styles: { width: "8%" },
                    formatter: (m, p) => (p ? i18n("general.yes") : i18n("general.no")),
                },
                {
                    name: i18n("general.fields.created"),
                    property: "created",
                    styles: { width: "10%" },
                    formatter: (m, p) => DateTime.toPreciseDateTimeString(p),
                },
                {
                    name: i18n("subscription.fields.validUntil"),
                    property: "validUntil",
                    styles: { width: "8%" },
                    formatter: (m, p) => {
                        return p === undefined ? i18n("subscription.unrestricted") : DateTime.toDateString(p);
                    },
                },
                {
                    name: i18n("general.fields.description"),
                    property: "description",
                },
                {
                    name: "",
                    property: "id",
                    styles: { width: "110px", "text-align": "right" },
                    formatter: () => {
                        return $(
                            '<span><a class="btn btn-sm btn-clean btn-icon btn-icon-md"><i class="la la-edit"></i></a></span>'
                        );
                    },
                },
            ],
            locationDependend: true,
            filterProvider: {
                getFilter: () => this.search.getValue(),
                setFilter: (d) => this.search.setValue(d),
            },
        };

        this.search = new TableFilter().appendTo(this.$element.find("> .kt-portlet__body.tablefilter"));
        this.search.addDropdownFilter(
            "locationId",
            Factory.getInstance<LocationDao>(LocationDao),
            false,
            i18n("subscription.allLocations"),
            undefined,
            (e) => { return getLocationName(e)}
        );

        this.dataTable = new DataTable<ISubscription>(
            this.$element.find("> .kt-portlet__body > .kt-datatable"),
            this.dao,
            tableConfig
        );
        this.dataTable.onClick(
            (m) => {
                location.href = "/subscription/" + m.id;
            },
            (m) => {
                window.open("/subscription/" + m.id);
            }
        );

        this.dataTable.listenTo(this.search);
    }
}
